import { openReasonPopover, openStagePicker, REJECT } from "./popovers";
import { applyResponse, pageData, PipelineStageData, pipelineRoot, sendJson } from "./request";

const MAX_SELECTED = 500;

function storageKey(list: HTMLElement): string {
  return `pl-open:${list.dataset.listKey || "x"}`;
}

function openGroups(list: HTMLElement): Set<string> {
  try {
    const saved = localStorage.getItem(storageKey(list));
    if (saved) return new Set(JSON.parse(saved) as string[]);
  } catch {
    // storage unavailable: use the defaults
  }
  return new Set((list.dataset.defaultOpen || "").split(",").filter(Boolean));
}

function saveOpen(list: HTMLElement, open: Set<string>): void {
  try {
    localStorage.setItem(storageKey(list), JSON.stringify(Array.from(open)));
  } catch {
    // ignore
  }
}

function setGroup(group: HTMLElement, open: boolean): void {
  group.classList.toggle("is-closed", !open);
  group.querySelector("[data-pl-group-toggle]")?.setAttribute("aria-expanded", String(open));
}

export function bindList(root: HTMLElement | null = pipelineRoot()): void {
  const list = root?.querySelector<HTMLElement>("[data-pl-list]");
  if (!list) return;
  const open = openGroups(list);
  list.querySelectorAll<HTMLElement>("[data-pl-group]").forEach((group) => setGroup(group, open.has(group.dataset.plGroup!)));
  refreshBulk(list);
}

function selected(list: HTMLElement): string[] {
  return Array.from(list.querySelectorAll<HTMLInputElement>("[data-pl-select]:checked")).map((box) => box.value);
}

function refreshBulk(list: HTMLElement): void {
  const ids = selected(list);
  const bar = list.querySelector<HTMLElement>("[data-pl-bulk]");
  if (!bar) return;
  bar.hidden = ids.length === 0;
  const tooMany = ids.length > MAX_SELECTED;
  bar.querySelector<HTMLElement>("[data-pl-bulk-count]")!.textContent = tooMany
    ? `${ids.length} selected · select at most ${MAX_SELECTED}`
    : `${ids.length} selected`;
  bar.querySelectorAll<HTMLButtonElement>("[data-pl-bulk-move], [data-pl-bulk-advance], [data-pl-bulk-reject]").forEach((button) => {
    button.disabled = tooMany;
  });
  list.querySelectorAll<HTMLElement>("tr[data-pl-card]").forEach((row) => {
    row.classList.toggle("is-selected", !!row.querySelector<HTMLInputElement>("[data-pl-select]:checked"));
  });
}

function selectedStages(list: HTMLElement): PipelineStageData[] | undefined {
  const byBot = pageData().stages_by_bot;
  if (pipelineRoot()?.dataset.context !== "ats" || !byBot) return undefined;

  const botIds = new Set(
    Array.from(list.querySelectorAll<HTMLInputElement>("[data-pl-select]:checked"))
      .map((box) => box.closest<HTMLElement>("[data-pl-card]")?.dataset.botId)
      .filter((id): id is string => !!id),
  );
  const seen = new Set<string>();
  const stages = Array.from(botIds).reduce<PipelineStageData[]>((all, id) => all.concat(byBot[id] || []), []);
  const unique = stages.filter((stage) => {
    const key = stage.name.trim().toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  return unique.filter((stage) => stage.kind !== "final").concat(unique.filter((stage) => stage.kind === "final"));
}

async function bulk(list: HTMLElement, body: Record<string, unknown>): Promise<void> {
  const root = pipelineRoot();
  const result = await sendJson(root!.dataset.bulkUrl!, "PATCH", { ...body, ids: selected(list) });
  if (applyResponse(result)) bindList(root);
}

let bound = false;

export function initListActions(): void {
  if (bound) return;
  bound = true;
  document.addEventListener("click", (event) => {
    const target = event.target as HTMLElement;
    const list = target.closest<HTMLElement>("[data-pl-list]");
    if (!list) return;
    const toggle = target.closest<HTMLElement>("[data-pl-group-toggle]");
    if (toggle) {
      const group = toggle.closest<HTMLElement>("[data-pl-group]")!;
      const open = openGroups(list);
      const nowOpen = group.classList.contains("is-closed");
      if (nowOpen) open.add(group.dataset.plGroup!);
      else open.delete(group.dataset.plGroup!);
      saveOpen(list, open);
      setGroup(group, nowOpen);
      return;
    }
    const button = target.closest<HTMLElement>("[data-pl-bulk-move], [data-pl-bulk-advance], [data-pl-bulk-reject], [data-pl-bulk-clear]");
    if (!button) return;
    event.stopPropagation();
    if (button.matches("[data-pl-bulk-clear]")) {
      list.querySelectorAll<HTMLInputElement>("[data-pl-select], [data-pl-check-all]").forEach((box) => (box.checked = false));
      refreshBulk(list);
    } else if (button.matches("[data-pl-bulk-advance]")) {
      void bulk(list, { bulk_action: "advance" });
    } else if (button.matches("[data-pl-bulk-reject]")) {
      openReasonPopover(button, "Reason", (reasonId) => void bulk(list, { bulk_action: "reject", exit_reason_id: reasonId }));
    } else {
      openStagePicker(button, null, (value) => {
        if (value === REJECT) openReasonPopover(button, "Reason", (reasonId) => void bulk(list, { bulk_action: "reject", exit_reason_id: reasonId }));
        else void bulk(list, { bulk_action: "move", stage_id: value });
      }, { stages: selectedStages(list) });
    }
  });
  document.addEventListener("change", (event) => {
    const box = event.target as HTMLInputElement;
    const list = box.closest?.<HTMLElement>("[data-pl-list]");
    if (!list) return;
    if (box.matches("[data-pl-check-all]")) {
      list
        .querySelectorAll<HTMLInputElement>("[data-pl-group]:not(.is-closed) [data-pl-select]")
        .forEach((row) => (row.checked = box.checked));
    }
    if (box.matches("[data-pl-select], [data-pl-check-all]")) refreshBulk(list);
  });
  document.addEventListener("keydown", (event) => {
    const toggle = (event.target as HTMLElement).closest?.<HTMLElement>("[data-pl-group-toggle]");
    if (toggle && (event.key === "Enter" || event.key === " ")) {
      event.preventDefault();
      toggle.click();
    }
  });
}
